import React, { useEffect, useRef, useState } from 'react';
import { MainLayout } from '../../../../components/MainLayout';
import { PagesHeader } from '../../../../components/shared/PagesHeader';
import { useTranslation } from 'react-i18next';
import { ROUTES } from './ReviewBillingAgreement.config';
import { Formik } from 'formik';
import { addNewBillingAgreementInitialValues } from '../../config/initialValues';
import { reviewBillingAgreementValidationSchema } from '../../config/validation';
import {
  reviewBillingAgreementFormConfig,
  termsAndConditionsFieldConfig,
} from './formConfig';
import styles from './ReviewBillingAgreement.styles.scss';
import BillingAgreementForm from '../../components/BillingAgreementForm/BillingAgreementForm';
import { useAuthentication } from '../../../../hooks/useAuthentication';
import { Button } from 'primereact/button';
import {
  approveSpecialAgreement,
  approveSpecialAgreementAmendment,
  getSpecialAgreementAmendments,
  getSpecialAgreementInfo,
  rejectSpecialAgreement,
  rejectSpecialAgreementAmendment,
} from '../../../../services/apis';
import { useNavigate, useParams } from 'react-router';
import { ACTING_AS } from '../../config/constants';
import RejectBillingAgreementDialog from '../../components/RejectBillingAgreementDialog/RejectBillingAgreementDialog';
import ApproveBillingAgreementDialog from '../../components/ApproveBillingAgreementDialog/ApproveBillingAgreementDialog';
import { AddNewBillingTerms } from '../../components/AddNewBillingTerms/AddNewBillingTerms';
import { mapSpecialBillingAgreementToFormValues } from '../../../../helpers/specialBillingHelpers';

const ReviewBillingAgreement = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();

  const formRef = useRef();

  const { userInfo } = useAuthentication();

  const [formInitValues, setFormInitValues] = useState(
    addNewBillingAgreementInitialValues({}),
  );

  const [actingAs, setActingAs] = useState(ACTING_AS.SELLER);
  const [readOnly, setReadOnly] = useState(false);
  const [agreementType, setAgreementType] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [isAmendment, setIsAmendment] = useState(false);
  const [updateAmendment, setAmendment] = useState();
  const [showTerms, setShowTerms] = useState(false);

  const checkAmendments = (agreement) => {
    getSpecialAgreementAmendments(params.id).then(({ data }) => {
      if (!data) {
        return;
      }
      const amednments =
        data?.filter((amendment) => amendment.status === 'Pending') ?? [];

      if (amednments.length > 0) {
        setIsAmendment(true);

        const reversedAgreements = mapSpecialBillingAgreementToFormValues(
          amednments[0],
          agreement,
          agreement.agreementType,
        );

        setFormInitValues(reversedAgreements);
        setAmendment(amednments[0]);
        if (userInfo.userId === amednments[0].requestedByUser) {
          setReadOnly(true);
        } else {
          setShowTerms(true);
        }
      }
    });
  };

  const onApprove = () => {
    return isAmendment
      ? approveSpecialAgreementAmendment(params.id, updateAmendment.amendmentId)
      : approveSpecialAgreement(params.id);
  };

  const onReject = (reason) => {
    return isAmendment
      ? rejectSpecialAgreementAmendment(
          params.id,
          updateAmendment.amendmentId,
          reason,
        )
      : rejectSpecialAgreement(params.id, reason);
  };

  useEffect(() => {
    if (params.id) {
      getSpecialAgreementInfo(params.id)
        .then(({ data }) => {
          const reversedAgreements = mapSpecialBillingAgreementToFormValues(
            data,
            {},
            data.agreementType,
          );
          setAgreementType(data.agreementType);
          setFormInitValues(reversedAgreements);
          if (userInfo.userId === data.createdByUser) {
            setReadOnly(true);
          } else if (userInfo.tax_number === data.buyerId) {
            setActingAs(ACTING_AS.BUYER);
            setShowTerms(true);
          } else if (userInfo.tax_number === data.thirdPartyId) {
            setActingAs(ACTING_AS.THIRD);
            setShowTerms(true);
          }
          return data;
        })
        .then(checkAmendments)
        .catch(() => {
          navigate('..');
        });
    }
  }, []);

  return (
    <MainLayout className={styles.reviewAgreement} eInvoicing={true}>
      <PagesHeader
        withBreadcrumb={true}
        routes={ROUTES({ agreementType, actingAs })}
      />
      {showTerms && (
        <AddNewBillingTerms
          onConfirm={setShowTerms}
          agreementType={agreementType}
          actingAs={actingAs}
          review={true}
        />
      )}
      {!showTerms && (
        <div className={styles.container}>
          <Formik
            innerRef={formRef}
            validateOnMount
            enableReinitialize
            validationSchema={reviewBillingAgreementValidationSchema({
              t,
              actingAs,
              agreementType,
            })}
            initialValues={formInitValues}
            onSubmit={(values) => {}}
          >
            {({
              values,
              errors,
              touched,
              isValid,
              setFieldValue,
              setTouched,
              setValues,
              submitForm,
            }) => {
              return (
                <BillingAgreementForm
                  values={values}
                  errors={errors}
                  isValid={isValid}
                  touched={touched}
                  createAs={actingAs}
                  hideMoreAgreements
                  agreementType={agreementType}
                  submitForm={submitForm}
                  setValues={setValues}
                  formConfig={reviewBillingAgreementFormConfig({
                    t,
                    lang: i18n.language,
                    values,
                    setTouched,
                    setFieldValue,
                    actingAs,
                    agreementType,
                    readOnly,
                  })}
                  termsAndConditionsFieldConfig={
                    readOnly
                      ? null
                      : termsAndConditionsFieldConfig({
                          t,
                          lang: i18n.language,
                          values,
                          setTouched,
                          setFieldValue,
                          agreementType,
                        })
                  }
                  formActionsStyles={styles.formActionsStyles}
                  actionButtons={
                    readOnly ? null : (
                      <div className={styles.actions}>
                        <Button
                          outlined
                          size="medium"
                          className={styles.formBtn}
                          type="button"
                          onClick={() => {
                            setShowRejectDialog(true);
                          }}
                          disabled={!values.termsAndConditions}
                        >
                          <span className="pi pi-times" />
                          <span>{t('special_billing.reject')}</span>
                        </Button>

                        <Button
                          type="button"
                          size="medium"
                          className={styles.formBtn}
                          disabled={
                            !values.termsAndConditions ||
                            !values.allFilesDownloaded
                          }
                          onClick={() => {
                            setShowApproveDialog(true);
                          }}
                        >
                          <span className="pi pi-check" />
                          <span>{t('special_billing.approve')}</span>
                        </Button>
                      </div>
                    )
                  }
                />
              );
            }}
          </Formik>
          <RejectBillingAgreementDialog
            setShowDialog={setShowRejectDialog}
            showDialog={showRejectDialog}
            onConfirm={onReject}
            isAmendment={isAmendment}
            agreementType={agreementType}
          />
          <ApproveBillingAgreementDialog
            setShowDialog={setShowApproveDialog}
            showDialog={showApproveDialog}
            onConfirm={onApprove}
            isAmendment={isAmendment}
            agreementType={agreementType}
          />
        </div>
      )}
    </MainLayout>
  );
};

export default ReviewBillingAgreement;
